import { QueryClient } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";

/**
 * Offline-first React Query client.
 *
 * - Cache is persisted to IndexedDB via idb-keyval, so listing screens
 *   (patients, prescriptions, appointments, etc.) rehydrate instantly
 *   from disk when the app opens without a network connection.
 * - Stale queries are still served from cache so the UI is never blank
 *   while offline. When online, react-query revalidates in the background.
 * - Prescription capture while offline is handled by the IndexedDB
 *   outbox in src/lib/prescription-outbox.ts (already installed).
 */

const CACHE_KEY = "doctorsx-query-cache";
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Serve cache first while revalidating in the background.
      staleTime: 1000 * 60, // 1 minute
      gcTime: MAX_AGE_MS,
      // Never throw the UI into a blank state just because we're offline.
      networkMode: "offlineFirst",
      retry: (failureCount, error: unknown) => {
        // Don't spam retries when the browser reports offline.
        if (typeof navigator !== "undefined" && !navigator.onLine) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      networkMode: "offlineFirst",
    },
  },
});

const asyncStorage = {
  getItem: (key: string) => get<string>(key).then((v) => v ?? null),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
};

const persister = createAsyncStoragePersister({
  storage: asyncStorage,
  key: CACHE_KEY,
  throttleTime: 1000,
});

// Kick off persistence once at module load. Safe in Electron (file://) and
// in the browser. Guarded so SSR / test envs don't crash.
if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
  persistQueryClient({
    queryClient,
    persister,
    maxAge: MAX_AGE_MS,
    // Bump this when the query shape changes materially.
    buster: "v1",
  });
}
